import { FC } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { DropDownChevronDownIcon } from 'assets/icons';
import { JobStatus, JobsStatuses } from '../constant';
import { useAppSelector } from 'hooks';
import { selectCurrentTeamJobPermissions, selectMerchantCustomizations } from 'containers/app/appSlice';
import { getJobStatusColor } from '../utils';

export interface StatusChangeOption {
  id: string;
  label: string;
  value: JobStatus;
  action: (status: JobStatus) => void | Promise<void>;
}

interface ChangeStatusDropdownProps {
  onStatusChange: (status: JobStatus) => void | Promise<void>;
  isDisabled?: boolean;
  className?: string;
  isLoading?: boolean;
}

const ChangeStatusDropdown: FC<ChangeStatusDropdownProps> = ({
  onStatusChange,
  isDisabled = false,
  className = '',
  isLoading = false
}) => {
  const currentJobPermissions = useAppSelector(selectCurrentTeamJobPermissions);
  const { teamConfigs } = useAppSelector(selectMerchantCustomizations);

  const getFilteredStatusOptions = (): StatusChangeOption[] => {
    let availableStatuses = [...JobsStatuses];
    const canMarkJobDone = currentJobPermissions?.closeOrMarkJobAsDone;
    const canManageOthersJobs = currentJobPermissions?.manageOthersJobs;
    const jobInitialStatus = teamConfigs?.jobInitialStatus;

    // Remove DONE status if user doesn't have permission to mark jobs as done
    if (!canMarkJobDone) {
      availableStatuses = availableStatuses.filter(statusItem => statusItem.value !== JobStatus.DONE);
    }

    // Remove INBOX status if user doesn't have manageOthersJobs permission or jobInitialStatus is not INBOX
    if (!canManageOthersJobs || jobInitialStatus !== JobStatus.INBOX) {
      availableStatuses = availableStatuses.filter(statusItem => statusItem.value !== JobStatus.INBOX);
    }

    return availableStatuses.map(status => ({
      id: status.value,
      label: status.label,
      value: status.value,
      action: async (statusValue: JobStatus) => await onStatusChange(statusValue)
    }));
  };

  const statusOptions = getFilteredStatusOptions();

  return (
    <Popover className={`relative ${className}`}>
      {({ open, close }) => (
        <>
          <Popover.Button
            disabled={isDisabled || isLoading}
            className={`flex h-fit items-center gap-2 p-1 text-sbase font-semibold text-secondary ${
              isDisabled || isLoading ? 'pointer-events-none opacity-50' : 'cursor-pointer'
            }`}>
            <span>Change status</span>
            <DropDownChevronDownIcon
              className={`path-fill-current h-[8px] w-[12px] text-secondary transition-transform 
                  ${open ? 'rotate-180' : ''}`}
            />
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1">
            <Popover.Panel
              className="absolute left-0 z-50 mt-1 w-56 rounded-md border border-borderGray
             bg-white shadow-lg">
              <div className="py-1">
                {statusOptions.map(option => (
                  <button
                    key={option.id}
                    onClick={async () => {
                      await option.action(option.value);
                      close();
                    }}
                    style={{ color: getJobStatusColor(option.value) }}
                    className="block w-full px-4 py-2 text-left text-sbase font-medium text-primaryText
                     hover:bg-gray-50 focus:bg-gray-50 focus:outline-none">
                    {option.label}
                  </button>
                ))}
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default ChangeStatusDropdown;
